/**
 * Subject-related types for Q4uDoc — schemas + inferred types.
 *
 * The schemas are authoritative; the TS types are derived via z.infer<>.
 * Consumers can import { SubjectWithCount, SubjectsResponse } for compile-time
 * typing, or { SubjectWithCountSchema, SubjectsResponseSchema } for runtime
 * validation of API responses.
 */

import { z } from 'zod';

export const SubjectWithCountSchema = z.object({
  name: z.string(),
  questionCount: z.number(),
  // The topics this specialty's blueprint declares (TOPIC_SPECIALTIES) that have questions,
  // with corpus-wide per-topic counts — derived from the actual corpus (not a static vocab), so
  // only topics that have questions appear. Grouped by the topic's blueprint owner rather than
  // each question's (re-tagged) `specialty`, so a topic never strays into another specialty's
  // list. Powers the quiz builder's specialty → topic drill-down with no empty selections.
  // Optional: additive, so pre-topics API responses (and clients that ignore it) still validate.
  topics: z.array(z.object({ name: z.string(), count: z.number() })).optional(),
});

export type SubjectWithCount = z.infer<typeof SubjectWithCountSchema>;

export const SubjectsResponseSchema = z.object({
  subjects: z.array(z.string()),
  subjectsWithCounts: z.array(SubjectWithCountSchema),
});

export type SubjectsResponse = z.infer<typeof SubjectsResponseSchema>;

export type SubjectName = string;
